import { type LeaveCompanyClosurePerson } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiRequestError } from '@/lib/api';
import { leavePanelClassName } from '@/products/leave/features/leave-panel';
import { formatLeaveDays } from '@/products/leave/features/preview-vacation-days';
import {
  useCreateLeaveCompanyClosure,
  useLeaveCapacityList,
  usePreviewLeaveCompanyClosure,
} from '@/products/leave/features/use-leave-admin';

type Draft = {
  nameDe: string;
  startsOn: string;
  endsOn: string;
  departmentIds: string[];
};

const EMPTY: Draft = { nameDe: '', startsOn: '', endsOn: '', departmentIds: [] };

export function LeaveCompanyClosureForm() {
  const departments = useLeaveCapacityList();
  const preview = usePreviewLeaveCompanyClosure();
  const create = useCreateLeaveCompanyClosure();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  const body = {
    nameDe: draft.nameDe.trim(),
    startsOn: draft.startsOn,
    endsOn: draft.endsOn,
    departmentIds: draft.departmentIds,
  };
  const currentKey = JSON.stringify(body);
  const shown = preview.data && previewKey === currentKey ? preview.data : null;
  const canCreate = shown?.people.some((person) => person.included) === true;

  function patch(next: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...next }));
  }

  function toggleDepartment(id: string, checked: boolean) {
    patch({
      departmentIds: checked
        ? [...draft.departmentIds, id]
        : draft.departmentIds.filter((current) => current !== id),
    });
  }

  async function onPreview(event: FormEvent) {
    event.preventDefault();
    if (body.departmentIds.length === 0) {
      toast.error('Bitte mindestens eine Abteilung wählen.');
      return;
    }
    try {
      await preview.mutateAsync(body);
      setPreviewKey(currentKey);
    } catch (error) {
      setPreviewKey(null);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Vorschau konnte nicht gerechnet werden.',
      );
    }
  }

  async function onCreate() {
    if (!canCreate) {
      return;
    }
    try {
      const created = await create.mutateAsync(body);
      toast.success(`${created.created} Anträge angelegt.`);
      setDraft(EMPTY);
      setPreviewKey(null);
      preview.reset();
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Betriebsurlaub konnte nicht angelegt werden.',
      );
    }
  }

  return (
    <form
      className={`${leavePanelClassName} space-y-4`}
      onSubmit={(event) => void onPreview(event)}
    >
      <h2 className="text-lg font-semibold">Betriebsurlaub</h2>
      <p className="text-sm text-muted-foreground">
        Die Vorschau rechnet die Urlaubstage. Anlegen schreibt erst danach genehmigte Anträge.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="closure-name">Name</Label>
          <Input
            id="closure-name"
            className="mt-2"
            value={draft.nameDe}
            onChange={(event) => patch({ nameDe: event.target.value })}
            maxLength={120}
            required
          />
        </div>
        <div>
          <Label htmlFor="closure-from">Von</Label>
          <Input
            id="closure-from"
            type="date"
            className="mt-2"
            value={draft.startsOn}
            onChange={(event) => patch({ startsOn: event.target.value })}
            required
          />
        </div>
        <div>
          <Label htmlFor="closure-to">Bis</Label>
          <Input
            id="closure-to"
            type="date"
            className="mt-2"
            value={draft.endsOn}
            onChange={(event) => patch({ endsOn: event.target.value })}
            required
          />
        </div>
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">Abteilungen</legend>
        {departments.isPending ? (
          <p className="text-sm text-muted-foreground">Abteilungen werden geladen …</p>
        ) : null}
        {departments.data?.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Abteilungen vorhanden.</p>
        ) : null}
        <ul className="space-y-2">
          {departments.data?.items.map((item) => (
            <li key={item.departmentId}>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.departmentIds.includes(item.departmentId)}
                  onCheckedChange={(checked) =>
                    toggleDepartment(item.departmentId, checked === true)
                  }
                />
                {item.departmentName}
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={preview.isPending}>
          Vorschau
        </Button>
        <Button
          type="button"
          disabled={!canCreate || create.isPending}
          onClick={() => void onCreate()}
        >
          Anlegen
        </Button>
      </div>
      {shown ? <PreviewList people={shown.people} /> : null}
    </form>
  );
}

function PreviewList({ people }: { people: LeaveCompanyClosurePerson[] }) {
  if (people.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Keine Personen in den gewählten Abteilungen.</p>
    );
  }
  return (
    <ul className="space-y-2">
      {people.map((person) => (
        <li key={person.userId} className="rounded-md border px-3 py-2 text-sm">
          <p className="font-medium">{person.displayName}</p>
          <p className="text-muted-foreground">
            {formatLeaveDays(person.days)} Tage · Rest {formatLeaveDays(person.rest)} · neuer Rest{' '}
            {formatLeaveDays(person.restAfter)}
          </p>
          {person.skipReason ? (
            <p className="text-amber-700 dark:text-amber-300">{skipLabel(person.skipReason)}</p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function skipLabel(reason: NonNullable<LeaveCompanyClosurePerson['skipReason']>): string {
  if (reason === 'balance') {
    return 'Zu wenig Rest, wird ausgelassen.';
  }
  if (reason === 'overlap') {
    return 'Überschneidet sich mit einem Antrag, wird ausgelassen.';
  }
  if (reason === 'capacity') {
    return 'Die Mindestbesetzung wäre unterschritten, wird ausgelassen.';
  }
  return 'Kein Urlaubstag in der Spanne, wird ausgelassen.';
}
